<template>
    <div class="d-flex flex-column" style="gap: 1rem">
        <slot name="filters" :search="search" v-if="!isFavorite">
            <nl-search-filters @search="search" />
        </slot>
        <slot name="results">
            <SearchResult
                v-if="results.length"
                :credit="credits"
                :display-current-position="displayCurrentPosition"
                :display-current-url="displayCurrentUrl"
                :loading="loading"
                :meta="meta"
                :modal-infos="modalInfos"
                :project-id="projectId"
                :results="results"
                :inner-hits-key="innerHitsKey"
                :is-favorite="isFavorite"
                @search="search(filters)"
            />
        </slot>
        <slot v-if="alreadySearched && results.length === 0" name="errors" :props="{ filters, loading }">
            <div v-if="loading" class="box p-5" style="position: relative">
                <b-loading :active="loading" :is-full-page="false" />
            </div>
            <b-notification v-else-if="hasError" active :closable="false" type="is-danger is-light">
                {{ $t('error.message.default') }}
            </b-notification>
            <Card
                v-else-if="filters.keyword"
                v-show="!loading"
                :keyword="filters.keyword"
                :email="project.destinataires_mails"
                :lang="filters.lang === 'en' ? 'com_en' : 'fr_fr'"
            />
            <b-notification v-else active :closable="false" type="is-secondary is-light">
                {{ $t('searchform.enter-keyword') }}
            </b-notification>
        </slot>
    </div>
</template>

<script>
    import NlSearchFilters from '@/components/Search/NlSearchFilters'
    import { mapActions, mapMutations, mapState } from 'vuex'
    import Card from '@/components/Search/Card.vue'
    import SearchResult from '@/components/Search/SearchResult.vue'
    import ReadUrlsByRequest from '@/domain/valueObject/elastic/ReadUrlsByRequest'
    import ReadUrlsByFavorites from '@/domain/valueObject/elastic/ReadUrlsByFavorites'
    import search from '@/store/modules/search'

    export default {
        name: 'SearchLayout',
        props: {
            getData: {
                type: Function,
                required: true,
                default: () => {}
            },
            displayCurrentPosition: { type: Boolean, default: false },
            displayCurrentUrl: { type: Boolean, default: false },
            isFavorite: { type: Boolean, default: false },
            innerHitsKey: {
                type: String,
                default: 'keywords_hits'
            },
            searchAtCreation: { type: Boolean, default: false }
        },
        components: {
            SearchResult,
            Card,
            NlSearchFilters
        },
        data() {
            return {
                keyword: null,
                wordExcluded: ['le', 'la', 'l', 'c', 'pour', 'a', 'de', 'sans', 'une', 'un', 'ou', 'des', 'les', 'en'],
                isSearchExact: false,
                orderBy: 'DESC',
                mainThematic: null,
                modalInfos: {
                    content: null,
                    info: null
                },

                loading: false,
                hasError: false,
                results: [],
                alreadySearched: false
            }
        },
        computed: {
            ...mapState('search', {
                filters: 'filters',
                meta: 'meta'
                // results: 'results'
            }),
            ...mapState('auth', {
                credits: state => state.user?.credit_amount
            }),
            projectId() {
                return this.$store.state.project ? this.$store.state.project.id : null
            },
            project() {
                return this.$store.state.project
            }
        },
        watch: {
            projectId: {
                immediate: true,
                handler() {
                    this.handleProjectChange()
                }
            }
        },
        mounted() {
            const query = this.$route.query

            const hasSearchParam = Object.keys(query).length > 0

            if (hasSearchParam) {
                const params = {
                    ...this.filters,
                    ...query
                }

                this.search(params)
            }
        },
        methods: {
            ...mapActions('search', {
                resetSearch: 'resetSearch'
            }),
            ...mapActions('favorites', { getFavorites: 'index' }),
            ...mapActions('config', ['getModalInfo']),
            ...mapMutations('search', {
                setMeta: 'setMeta'
            }),
            search(params) {
                const readUrlsByRequest = this.isFavorite
                    ? ReadUrlsByFavorites.fromParams(this.projectId, this.meta)
                    : ReadUrlsByRequest.fromParams(params, this.meta, this.projectId)

                this.loading = true

                this.getData(readUrlsByRequest)
                    .then(({ data }) => {
                        this.results = data.results
                        this.loading = false
                        this.hasError = false
                        this.alreadySearched = true
                        this.setMeta({ total: data.total })
                    })
                    .catch(e => {
                        this.results = []
                        this.loading = false
                        this.hasError = true
                        this.$store.dispatch('toastError', e)
                        this.alreadySearched = true
                        this.setMeta({ total: 0 })
                    })
            },
            async handleProjectChange() {
                if (this.isFavorite) {
                    this.search(this.filters)
                }
                await this.getFavorites({
                    projectId: this.projectId
                })
            }
        },
        async created() {
            this.getModalInfo().then(({ modal_bloc_message, information }) => {
                this.modalInfos.content = modal_bloc_message
                this.modalInfos.info = information
                if (this.searchAtCreation && (this.filters.thematic.length > 0 || this.isFavorite)) {
                    this.search(this.filters)
                }
            })
        },
        beforeDestroy() {
            this.resetSearch()
        }
    }
</script>
